/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/proposals.md and
 * documents.md Marketing Constraints.
 *
 * Portal reality (verified during audit):
 *   - src/app/portal/[orgId]/ has: page.tsx, layout.tsx, portal-shell.tsx,
 *     dashboard/, proposals/, invoices/, projects/, documents/
 *   - Proposal accept/decline with feedback works
 *   - Engagement tracking (view count, time spent) is live
 *   - WON auto-transition wired on all 3 acceptance paths
 *     (authenticated, portal, share-link)
 *   - Invoice payment via Stripe — gated, see billing-stripe.md
 *   - Document portal-visible flag supported
 *
 * IP to protect:
 *   - pricing_data_snapshot, proposal layout internals
 *   - share-link token generation
 *   - engagement tracking mechanics
 *
 * Do NOT:
 *   - Promise user template upload — bug #363 deferred post-launch
 *   - Reveal PricingDisplayConfig duplication or internal fields
 *   - Market Stripe as always-on — requires org setup
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Globe,
  Share2,
  Eye,
  CheckCircle2,
  XCircle,
  CreditCard,
  FileText,
  FolderOpen,
  Briefcase,
  DollarSign,
  Calculator,
  Users,
  MessageSquare,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Client Portal",
  title: "A client portal",
  titleHighlight: "your customers will actually use.",
  description:
    "Give clients a simple place to review proposals, respond faster, pay invoices where enabled, and find shared project documents without long email chains.",

  featureGrid: {
    title: "Everything a client actually needs from you",
    items: [
      {
        icon: FileText,
        title: "Proposal Viewer",
        description:
          "Clients review proposals in a clean, branded viewer with pricing breakdowns, scope details, and your company branding. Multiple proposal layouts are available when you send.",
      },
      {
        icon: CheckCircle2,
        title: "Accept With Feedback",
        description:
          "Clients can approve in one click and leave notes, giving your team faster decisions and cleaner handoffs.",
      },
      {
        icon: XCircle,
        title: "Decline With Reasons",
        description:
          "If a client declines, they can leave a reason. You get the feedback directly, the proposal is logged as declined, and you have the context to follow up or revise.",
      },
      {
        icon: Eye,
        title: "Engagement Tracking",
        description:
          "Proposal engagement signals show when clients are reviewing so your team can follow up at the right time.",
      },
      {
        icon: CreditCard,
        title: "Invoice Payment",
        description:
          "Clients pay invoices through the portal where online payments are enabled, and payment status stays in sync.",
      },
      {
        icon: FolderOpen,
        title: "Shared Documents",
        description:
          "Choose which documents clients can view in the portal. Specs, permits, photos, and closeout files stay read-only for client access.",
      },
      {
        icon: Briefcase,
        title: "Project Dashboard",
        description:
          "Clients see their active projects and recent activity in a dedicated dashboard. Transparency without giving them the whole back office.",
      },
      {
        icon: Share2,
        title: "Share Link Access",
        description:
          "Share proposals with low-friction access so clients can review quickly without extra setup.",
      },
      {
        icon: Globe,
        title: "Authenticated Repeat Access",
        description:
          "Repeat clients can use authenticated access for longer-term history and visibility.",
      },
    ],
  },

  workflow: {
    title: "How a client experiences the portal",
    description:
      "A cleaner client journey from review to response and payment.",
    steps: [
      {
        label: "Receive",
        title: "The client gets a link — no login required",
        description:
          "Send proposal access with minimal friction so review starts quickly.",
        details: [
          "Share-link access path",
          "Email-friendly delivery",
          "Secure access workflow",
          "Cross-device compatibility",
          "Faster first review",
        ],
      },
      {
        label: "Review",
        title: "Proposal viewer with tracked engagement",
        description:
          "Clients review branded proposal content while your team gets visibility into engagement signals for smarter follow-up.",
        details: [
          "Branded proposal presentation",
          "Pricing and scope clarity",
          "Engagement-signal visibility",
          "Open activity awareness",
          "Better follow-up timing",
        ],
      },
      {
        label: "Decide",
        title: "Accept or decline — one click",
        description:
          "Make approval and decline simple so decision cycles are shorter and feedback loops are clearer.",
        details: [
          "Low-friction accept/decline path",
          "Optional decline feedback",
          "Sales status continuity on acceptance",
          "Fast team notification context",
          "Smoother handoff into execution",
        ],
      },
      {
        label: "Pay",
        title: "Invoices paid online through the portal",
        description:
          "Where online payment is configured, clients can settle invoices in the portal and teams retain clean payment visibility.",
        details: [
          "Online payment support where enabled",
          "Safe payment-recording behavior",
          "Invoice status continuity",
          "Retainage-aware financial handling",
          "Client-facing payment history context",
        ],
      },
      {
        label: "Revisit",
        title: "Repeat clients get the full history",
        description:
          "Authenticated portal access supports long-term clients who need project and document history at their fingertips.",
        details: [
          "Repeat-client authentication path",
          "Proposal and invoice history access",
          "Centralized shared document visibility",
          "Project history context",
          "Reduced resend requests",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Share2,
      title: "Share Link Access — No Login Friction",
      description:
        "The fastest way to slow down a signed deal is adding extra login steps. Share-link access lets clients open and review proposals quickly, then approve or decline without account friction. For repeat clients who want long-term history, authenticated portal access is still available.",
      bullets: [
        "Secure share links per proposal",
        "Zero login friction",
        "Works on any device, any browser",
        "Acceptance tracked and logged even without auth",
        "Authenticated portal available as opt-in for repeat clients",
      ],
    },
    {
      icon: Eye,
      title: "Engagement Tracking That Answers 'Did They See It?'",
      description:
        "Use proposal engagement signals to understand review activity and time follow-up conversations more effectively.",
      bullets: [
        "View count per proposal",
        "Total time spent in the viewer",
        "Engagement-depth visibility",
        "Real-time notification to your team on first open",
        "Visible in the proposal detail page for your whole team",
      ],
    },
    {
      icon: CheckCircle2,
      title: "Acceptance Wired To The Estimate Automatically",
      description:
        "When a client accepts, pipeline status updates automatically so your team can move straight from sales to execution without manual cleanup.",
      bullets: [
        "Accept / decline action on every proposal",
        "Pipeline advances to Won automatically across share-link, portal, and authenticated paths",
        "Optional feedback on decline",
        "Team notifications on transition",
        "Project / service creation ready immediately after acceptance",
      ],
    },
    {
      icon: CreditCard,
      title: "Invoice Payment Without Double-Booking",
      description:
        "Clients can pay invoices through the portal where online payments are configured, with safeguards that reduce duplicate entries and reconciliation headaches.",
      bullets: [
        "Online payment integration (requires org setup)",
        "Payment-entry safeguards to prevent duplicate records",
        "Invoice status auto-updates on payment",
        "Retainage release flows through the same system",
        "Manual payment recording (check, cash) also supported",
      ],
    },
  ],

  connections: {
    title: "How the client portal connects to the rest of CC",
    items: [
      {
        icon: FileText,
        flow: "Proposal → Portal → Acceptance",
        description:
          "Send a proposal via email or share link. The client reviews, accepts or declines, and the estimate moves to Won automatically across all three paths.",
      },
      {
        icon: DollarSign,
        flow: "Invoice → Portal → Online Payment",
        description:
          "Invoices land in the portal and clients can pay where online payments are enabled, with payment status updating automatically.",
      },
      {
        icon: FolderOpen,
        flow: "Document → Portal-Visible Flag → Client Access",
        description:
          "Mark any document for client access and it appears in the portal as read-only content.",
      },
      {
        icon: Eye,
        flow: "Portal View → Engagement → Notification",
        description:
          "Every proposal view fires engagement signals so teams can see opens and review time context for better follow-up timing.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Proposals & Portal",
      href: "/features/proposals-portal",
      icon: FileText,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Estimates & Takeoffs",
      href: "/features/estimates",
      icon: Calculator,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: FolderOpen,
    },
    {
      label: "CRM",
      href: "/features/crm",
      icon: Users,
    },
    {
      label: "Communications",
      href: "/features/email-hub",
      icon: MessageSquare,
    },
  ],
};

export function ClientPortalPageClient() {
  return <FeaturePageTemplate config={config} />;
}
